// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { consoleService } from '../../services/consoleService';
import { ContainerConfig } from '../../types/containerSettings';
import { LeaderboardRecordList, LeaderboardRecord } from '../../types/leaderboards';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../ConfirmDialog';
import './ContainerSettings.scss';

interface InvestaPlaySettings {
  alert_message: string;
  last_bundle_version: number;
  minimum_supported_bundle_version: number;
}

type TabType = 'settings' | 'leaderboard';

const ContainerSettings: React.FC = () => {
  const [config, setConfig] = useState<ContainerConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('settings');
  
  // InvestaPlay settings form fields
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [lastBundleVersion, setLastBundleVersion] = useState<number>(1);
  const [minimumSupportedBundleVersion, setMinimumSupportedBundleVersion] = useState<number>(1);
  
  // Leaderboard state
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardRecordList | null>(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardError, setLeaderboardError] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);
  const { dialogState, openConfirmDialog, closeConfirmDialog, handleConfirm } = useConfirmDialog();

  const globalLeaderboardId = 'total_earned_coins_global';

  useEffect(() => {
    loadContainerConfig();
  }, []);

  useEffect(() => {
    if (activeTab === 'leaderboard') {
      loadLeaderboardData();
    }
  }, [activeTab]);

  const loadContainerConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Call game_config_get RPC function
      const rpcResponse = await consoleService.callRpcFunction({
        id: 'game_config_get',
        body: '{}',
        user_id: '',
        http_key: ''
      });
      
      // Parse the response body
      const configData = JSON.parse(rpcResponse.body || '{}');
      
      console.log('RPC Response:', configData);
      
      // Set a simple config object for compatibility
      setConfig({ name: 'investaplay-config' } as ContainerConfig);
      // Load InvestaPlay settings from RPC response
      const investaPlaySettings = configData;
      setAlertMessage(investaPlaySettings.alert_message || '');
      setLastBundleVersion(investaPlaySettings.last_bundle_version || 1);
      setMinimumSupportedBundleVersion(investaPlaySettings.minimum_supported_bundle_version || 1);
      
      // Game settings are no longer needed in this component
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load game configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadLeaderboardData = async () => {
    try {
      setLeaderboardLoading(true);
      setLeaderboardError(null);
      
      const data = await consoleService.getGlobalCoinsLeaderboard(50);
      setLeaderboardData(data);
    } catch (err) {
      setLeaderboardError(err instanceof Error ? err.message : 'Failed to load leaderboard data');
    } finally {
      setLeaderboardLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setLoading(true);
      
      // Prepare InvestaPlay settings
      const investaPlaySettings: InvestaPlaySettings = {
        alert_message: alertMessage,
        last_bundle_version: lastBundleVersion,
        minimum_supported_bundle_version: minimumSupportedBundleVersion
      };
      
      // Call game_config_resetup RPC function with only InvestaPlay settings
      await consoleService.callRpcFunction({
        id: 'game_config_resetup',
        body: JSON.stringify(investaPlaySettings),
        user_id: '',
        http_key: ''
      });
      
      setHasUnsavedChanges(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save InvestaPlay configuration');
    } finally {
      setLoading(false);
    }
  };

  // Game settings change handler removed since we only handle InvestaPlay settings




  const handleInvestaPlayFieldChange = (field: keyof InvestaPlaySettings, value: string | number) => {
    switch (field) {
      case 'alert_message':
        setAlertMessage(value as string);
        break;
      case 'last_bundle_version':
        setLastBundleVersion(value as number);
        break;
      case 'minimum_supported_bundle_version':
        setMinimumSupportedBundleVersion(value as number);
        break;
    }
    setHasUnsavedChanges(true);
  };

  const handleDeleteRecord = (record: LeaderboardRecord, index: number) => {
    openConfirmDialog(
      'Remove Record',
      `Are you sure you want to remove the record for "${record.username || record.owner_id}"? This action cannot be undone.`,
      async () => {
        try {
          await consoleService.deleteLeaderboardRecord('', globalLeaderboardId, record.owner_id);
          await loadLeaderboardData();
          setLeaderboardError('Record removed successfully!');
          setTimeout(() => setLeaderboardError(null), 3000);
        } catch (err: any) {
          setLeaderboardError(err.response?.data?.message || err.message || 'Failed to remove record');
        }
      }
    );
  };

  const handleResetLeaderboard = () => {
    openConfirmDialog(
      'Reset Leaderboard',
      `Are you sure you want to reset the global coins leaderboard? This will delete all records and cannot be undone.`,
      async () => {
        setResetting(true);
        setLeaderboardError(null);
        try {
          await consoleService.leaderboardReset(globalLeaderboardId);
          await loadLeaderboardData();
          setLeaderboardError('Leaderboard reset successfully!');
          setTimeout(() => setLeaderboardError(null), 3000);
        } catch (err: any) {
          setLeaderboardError(err.response?.data?.message || err.message || 'Failed to reset leaderboard');
        } finally {
          setResetting(false);
        }
      }
    );
  };

  // Game settings validation removed

  if (loading) {
    return (
      <div className="container-settings">
        <div className="loading-spinner">Loading InvestaPlay settings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-settings">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={loadContainerConfig} className="btn btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }


  return (
    <div className="container-settings">
      <div className="container-settings-header">
        <h1>InvestaPlay Settings</h1>
        <p className="header-description">Manage application settings and game configurations</p>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <span className="tab-icon">⚙️</span>
          Settings
        </button>
        <button 
          className={`tab-button ${activeTab === 'leaderboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('leaderboard')}
        >
          <span className="tab-icon">🏆</span>
          Leaderboard
        </button>
      </div>

      <div className="container-settings-content">
        {/* InvestaPlay Settings Tab */}
        {activeTab === 'settings' && (
          <div className="settings-section">

            <div className="investaplay-fields-container">
              <div className="field-group">
                <label className="field-label">
                  Alert Message
                  <span className="field-description">System-wide alert message displayed to users</span>
                </label>
                <textarea
                  value={alertMessage}
                  onChange={(e) => handleInvestaPlayFieldChange('alert_message', e.target.value)}
                  className="form-control alert-textarea"
                  placeholder="Enter alert message for users (leave empty for no alert)"
                  rows={3}
                />
              </div>

              <div className="version-fields-row">
                <div className="field-group">
                  <label className="field-label">
                    Last Bundle Version
                    <span className="field-description">Current app bundle version number</span>
                  </label>
                  <input
                    type="number"
                    value={lastBundleVersion}
                    onChange={(e) => handleInvestaPlayFieldChange('last_bundle_version', parseInt(e.target.value) || 1)}
                    className="form-control version-input"
                    min="1"
                    placeholder="1"
                  />
                </div>

                <div className="field-group">
                  <label className="field-label">
                    Minimum Supported Version
                    <span className="field-description">Minimum app version required to access the service</span>
                  </label>
                  <input
                    type="number"
                    value={minimumSupportedBundleVersion}
                    onChange={(e) => handleInvestaPlayFieldChange('minimum_supported_bundle_version', parseInt(e.target.value) || 1)}
                    className="form-control version-input"
                    min="1"
                    max={lastBundleVersion}
                    placeholder="1"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <div className="leaderboard-section">
            <div className="section-header">
              <div className="header-content">
                <h2>🏆 Global Coins Leaderboard</h2>
                <p className="header-description">Top players by total earned coins across all games</p>
              </div>
              <div className="section-actions">
                <button 
                  onClick={handleResetLeaderboard} 
                  className="btn btn-danger" 
                  disabled={resetting || leaderboardLoading}
                >
                  {resetting ? 'Resetting...' : '🔄 Reset Leaderboard'}
                </button>
                <button onClick={loadLeaderboardData} className="btn btn-secondary" disabled={leaderboardLoading}>
                  <span className="btn-icon">🔄</span>
                  {leaderboardLoading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
            </div>

            {leaderboardLoading && (
              <div className="loading-spinner">Loading leaderboard...</div>
            )}

            {leaderboardError && (
              <div className="error-message">
                <p>{leaderboardError}</p>
                <button onClick={loadLeaderboardData} className="btn btn-primary">
                  Retry
                </button>
              </div>
            )}

            {leaderboardData && !leaderboardLoading && (
              <div className="leaderboard-container">
                <div className="leaderboard-table-container">
                  <table className="leaderboard-table">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Player</th>
                        <th>Total Coins</th>
                        <th>Last Updated</th>
                        <th style={{ width: '100px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboardData.records?.map((record, index) => (
                        <tr key={record.owner_id} className={index < 3 ? `rank-${index + 1}` : ''}>
                          <td className="rank-cell">
                            <span className="rank-number">
                              {index === 0 && '🥇'}
                              {index === 1 && '🥈'}
                              {index === 2 && '🥉'}
                              {index > 2 && (record.rank || index + 1)}
                            </span>
                          </td>
                          <td className="player-cell">
                            <div className="player-info">
                              <span className="player-name">{record.username || 'Anonymous'}</span>
                              <span className="player-id">{record.owner_id}</span>
                            </div>
                          </td>
                          <td className="score-cell">
                            <span className="coin-amount">
                              🪙 {parseInt(record.score).toLocaleString()}
                            </span>
                          </td>
                          <td className="time-cell">
                            {new Date(record.update_time).toLocaleDateString()}
                          </td>
                          <td className="actions-cell">
                            <button
                              type="button"
                              className="btn btn-sm btn-remove"
                              onClick={() => handleDeleteRecord(record, index)}
                              title="Remove record"
                            >
                              🗑️ Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {(!leaderboardData.records || leaderboardData.records.length === 0) && (
                  <div className="empty-state">
                    <span className="empty-icon">🏆</span>
                    <h3>No leaderboard data</h3>
                    <p>No players have earned coins yet.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Save Button - Only show for settings tab */}
      {activeTab === 'settings' && (
        <div className="settings-actions">
          <button 
            onClick={handleSaveConfig} 
            className="btn btn-primary save-button"
            disabled={loading || !hasUnsavedChanges}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
          
          {hasUnsavedChanges && (
            <div className="unsaved-changes-indicator">
              Unsaved changes
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        onConfirm={handleConfirm}
        onCancel={closeConfirmDialog}
      />
    </div>
  );
};

export default ContainerSettings;
